import React, { useEffect, useState } from "react";
import Pilot from "../../model/Pilot";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import UploadManager from "../../shared/UploadManager";
import FormInputImage from "../common/FormInputImage";
import ImgUtil from "../../utils/ImgUtil";
import UploadFileType from "../../model/enums/UploadFileType";
import AppSubSections from "../../utils/AppSubSections";
import UploadFileStatus from "../../model/enums/UploadFileStatus";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";

interface IPilotFormErrors {
  name: string;
  description: string;
}

const defaultFormErrors: IPilotFormErrors = {
  name: "",
  description: "",
};

function PilotAddEdit() {
  // read the ID from the url
  const { id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<Pilot>(Pilot.EmptyInstance());
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IPilotFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const [uploadManager] = useState(new UploadManager({ apiClient: apiClient }));

  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let pilot: Pilot | undefined = undefined;
        if (id) {
          pilot = await apiClient?.get<Pilot>({
            url: APIEndPoints.PILOTS.ONE(parseInt(id)),
          });
        }
        // set into the state all the information
        setFormData(pilot ? pilot : Pilot.EmptyInstance());
        setFormErrors(defaultFormErrors);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // get the errors from the form
    const errors = validatePilotForm(formData);

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    var uploadErrors = "";
    try {
      // upload the new icon, if it exists (situation managed by the UploadManager)
      const uploadResult = await uploadManager.uploadFiles();
      if (uploadResult instanceof Array) {
        for (let f = 0; f < uploadResult.length; f++) {
          if (uploadResult[f].status === UploadFileStatus.COMPLETE) {
            iconServerId = uploadResult[f].serverId;
          } else {
            uploadErrors += "ERROR uploading file: " + uploadResult[f].fileName;
            console.log("ERROR uploading file: ", uploadResult[f].fileName);
          }
        }
      }
    } catch (exception) {
      uploadErrors += " " + exception;
      console.log(exception);
    }

    if (uploadErrors !== "") {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: uploadErrors,
      });
      return;
    }

    var body = new Pilot(
      authManager.getUserID()!!,
      formData.name,
      formData.description,
      iconServerId,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id);
      apiClient
        ?.put({
          url: APIEndPoints.PILOTS.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Pilot record updated successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.PILOTS,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    } else {
      apiClient
        ?.post<Pilot>({ url: APIEndPoints.PILOTS.NEW, body: body })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Pilot record created successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.PILOTS,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.PILOTS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Pilot record deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.PILOTS }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the pilot record",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as Pilot);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    setFormData(formDataCopy as Pilot);
  };

  const onFormCancel = () => {
    navigate(SETTINGS.go_back({ section: AppSections.PILOTS }));
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = id ? "Edit pilot record" : "Create a new pilot";
  const confirmLabel = id ? "Update pilot" : "Create pilot";

  const deleteButton = id ? (
    <Button
      label="Delete pilot"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this pilot?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete pilot",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  if (loading) {
    return <Loading />;
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
    >
      <FormInputImage
        label="Picture"
        id="icon"
        placeholderImage=""
        value={ImgUtil.display_icon(formData.icon_path)}
        required={false}
        onChange={formIconChange}
      />

      <FormInputText
        label="Pilot name"
        id="name"
        value={formData.name}
        required={true}
        placeholder="Pilot"
        onChange={formInputChange}
        error={formErrors.name}
      />

      <FormTextArea
        label="Description"
        id="description"
        value={formData.description}
        required={true}
        onChange={formInputChange}
        error={formErrors.description}
      />

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validatePilotForm(formData: Pilot): IPilotFormErrors {
  let errors: IPilotFormErrors = { name: "", description: "" };
  errors.name =
    formData.name.length < 4
      ? "Pilot name should be at least 4 characters long"
      : "";
  errors.description =
    formData.description.length < 10
      ? "Pilot description should be at least 10 characters long"
      : "";

  return errors;
}

function isFormInvalid(errors: IPilotFormErrors): boolean {
  return errors.name !== "" || errors.description !== "";
}

export default PilotAddEdit;
